import {useEffect, useState} from "react";
import NiceModal from "@ebay/nice-modal-react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {Autocomplete, Switch} from "@mui/material";
import {zodResolver} from "@hookform/resolvers/zod";
import {Controller, useForm} from "react-hook-form";
import {useNavigate, useParams} from "react-router-dom";
import {toast} from "sonner";
import {
  Box,
  Card,
  Colors,
  Container,
  Grid,
  HtmlCodeEditor,
  IconButton,
  IconTrash,
  Text,
  Tooltip,
  formatHtml,
} from "@bounce/shared/ui";
import {IconChevronUp} from "@bounce/shared/ui/assets";
import AreYouSureDialog from "@bounce/shared/consumer/bounce-ui/components/Dialogs/AreYouSureDialog";
import {Button, MenuItem, Select, SyncButton, TextField, useSyncPhase} from "../../ui/components";
import {autocompleteChevronSx} from "../../ui/components/inputStyles";
import {useCreateSnippet} from "../../apis/requests/snippets/createSnippet";
import DeleteResourceDialog from "../../components/DeleteResourceDialog";
import {useFetchSnippetFromCio} from "../../apis/requests/snippets/fetchSnippetFromCio";
import {useGetSnippets} from "../../apis/requests/snippets/getSnippets";
import {useSyncSnippetFromCio} from "../../apis/requests/snippets/syncSnippetFromCio";
import {ReachSnippet} from "../../apis/requests/snippets/types";
import {useUpdateSnippet} from "../../apis/requests/snippets/updateSnippet";
import {useDebounce} from "../../hooks/useDebounce";
import {reachUrls} from "../../layout/menu-items";
import HtmlFrame from "../templates/template-preview/HtmlFrame";
import SnippetUsagePanel from "./SnippetUsagePanel";
import {SOURCES} from "../templates/templateFormSchema";
import {SNIPPET_LANGUAGE_LABELS, SNIPPET_SOURCE_DISPLAY} from "./snippetDisplay";
import {snippetElementReference, snippetUrl} from "./helpers";
import {EMPTY_SNIPPET, SNIPPET_LANGUAGES, SnippetFormValues, snippetFormSchema} from "./snippetFormSchema";

const BODY_MIN_ROWS = 18;
const PREVIEW_DEBOUNCE_MS = 400;

const FieldLabel = ({children}: {children: string}) => (
  <Text size={13} sx={{color: Colors.grey._700, mb: 0.5}}>
    {children}
  </Text>
);

const linkPayload = (snippet: ReachSnippet, sourceSnippetId: number | null) => ({
  id: snippet.id,
  name: snippet.name,
  element_name: snippet.element_name,
  source: snippet.source,
  language: snippet.language ?? "en",
  source_snippet_id: sourceSnippetId,
  body: snippet.body,
});

const SnippetLink = ({label, href}: {label: string; href: string}) => (
  <Box
    component="a"
    href={href}
    target="_blank"
    rel="noreferrer"
    sx={{display: "flex", alignItems: "center", gap: 0.5, width: "fit-content", textDecoration: "none"}}
  >
    <Text size={13} variant="inter500" sx={{color: Colors.blue._500, textDecoration: "underline"}}>
      {label}
    </Text>
    <OpenInNewIcon sx={{width: 14, height: 14, color: Colors.blue._500}} />
  </Box>
);

const SnippetFormPage = () => {
  const navigate = useNavigate();
  const {id} = useParams<{id?: string}>();
  const snippetId = id === undefined ? undefined : Number(id);
  const isEdit = snippetId !== undefined;

  const {data: snippets = [], isLoading} = useGetSnippets();
  const existing = isEdit ? snippets.find((snippet) => snippet.id === snippetId) : undefined;

  const {mutateAsync: createAsync, isPending: isCreating} = useCreateSnippet();
  const {mutateAsync: updateAsync, isPending: isUpdating} = useUpdateSnippet();
  const {mutateAsync: syncAsync, isPending: isSyncing} = useSyncSnippetFromCio();
  const {mutateAsync: fetchFromCioAsync} = useFetchSnippetFromCio();
  const isBusy = isCreating || isUpdating || isSyncing;

  const {phase: syncPhase, runSync} = useSyncPhase();
  const [showHtml, setShowHtml] = useState(true);

  const {control, handleSubmit, reset, watch, getValues, setValue, formState} = useForm<SnippetFormValues>({
    resolver: zodResolver(snippetFormSchema),
    defaultValues: EMPTY_SNIPPET,
  });

  useEffect(() => {
    if (!existing) return;
    reset({
      name: existing.name,
      element_name: existing.element_name ?? "",
      source: existing.source,
      language: existing.language ?? "en",
      source_snippet_id: existing.source_snippet_id,
      translation_snippet_id: snippets.find((snippet) => snippet.source_snippet_id === existing.id)?.id ?? null,
      body: formatHtml(existing.body),
    });
  }, [existing, snippets, reset]);

  const body = watch("body");
  const source = watch("source");
  const name = watch("name");
  const elementName = watch("element_name");
  const language = watch("language");
  const sourceSnippetId = watch("source_snippet_id");
  const translationSnippetId = watch("translation_snippet_id");
  const isSpanish = language === "es";

  const englishSnippets = snippets.filter((snippet) => snippet.language !== "es" && snippet.id !== snippetId);
  const spanishSnippets = snippets.filter((snippet) => snippet.language === "es" && snippet.id !== snippetId);
  const selectedSourceSnippet = englishSnippets.find((snippet) => snippet.id === sourceSnippetId) ?? null;
  const selectedTranslationSnippet = spanishSnippets.find((snippet) => snippet.id === translationSnippetId) ?? null;
  const linkedTranslationId = snippets.find((snippet) => snippet.source_snippet_id === snippetId)?.id ?? null;

  useEffect(() => {
    if (isEdit) return;
    setValue("element_name", snippetElementReference(name), {shouldDirty: false});
  }, [isEdit, name, setValue]);
  const previewHtml = useDebounce(body, PREVIEW_DEBOUNCE_MS);

  const onFormat = () => {
    const current = getValues("body");
    if (!current?.trim()) return;
    setValue("body", formatHtml(current), {shouldDirty: true});
  };

  const linkTranslation = async (ownerId: number, nextTranslationId: number | null) => {
    if (isSpanish || nextTranslationId === linkedTranslationId) return;
    const previous = spanishSnippets.find((snippet) => snippet.id === linkedTranslationId);
    const next = spanishSnippets.find((snippet) => snippet.id === nextTranslationId);
    if (previous) await updateAsync(linkPayload(previous, null));
    if (next) await updateAsync(linkPayload(next, ownerId));
  };

  const onSave = handleSubmit(async (values) => {
    const payload = {
      name: values.name.trim(),
      element_name: values.element_name.trim() || null,
      source: values.source,
      language: values.language,
      source_snippet_id: values.language === "es" ? values.source_snippet_id : null,
      body: values.body,
    };
    try {
      const saved = isEdit ? await updateAsync({id: snippetId, ...payload}) : await createAsync(payload);
      await linkTranslation(saved.id, values.translation_snippet_id);
      toast.success(`Snippet "${payload.name}" ${isEdit ? "saved" : "created"}`);
      navigate(reachUrls.snippets);
    } catch {}
  });

  const savedSnippetId = existing?.source === source ? snippetId : undefined;
  const cioLookup = elementName.trim() || name.trim();

  const syncSavedSnippet = async (id: number) => {
    const synced = await runSync(() => syncAsync({id}));
    if (!synced) return;
    toast.success(`Snippet "${synced.name}" synced from Customer.io`);
  };

  const syncIntoForm = async () => {
    if (!cioLookup) {
      toast.error("Enter a name or an element to sync from Customer.io");
      return;
    }
    const fetched = await runSync(() => fetchFromCioAsync({source, element_name: cioLookup}));
    if (!fetched) return;
    setValue("body", formatHtml(fetched.body), {shouldDirty: true});
    setShowHtml(true);
    toast.success("Snippet HTML pulled from Customer.io - review and save");
  };

  const onSyncFromCio = () => {
    const runIt = savedSnippetId === undefined ? syncIntoForm : () => syncSavedSnippet(savedSnippetId);
    if (savedSnippetId === undefined && !body.trim()) {
      runIt();
      return;
    }
    NiceModal.show(AreYouSureDialog, {
      onConfirm: runIt,
      headerTextId: "Sync from Customer.io?",
      contentTextId: `This replaces the HTML of "${cioLookup}" with the current version in Customer.io.${
        savedSnippetId === undefined ? "" : " Unsaved changes will be lost."
      }`,
      confirmTextId: "Sync",
    });
  };

  const onDelete = () => {
    if (!isEdit || !existing) return;
    NiceModal.show(DeleteResourceDialog, {
      kind: "snippet",
      resourceId: snippetId,
      resourceName: existing.name,
      onDeleted: () => navigate(reachUrls.snippets),
    });
  };

  if (isEdit && !isLoading && !existing) {
    return (
      <Box sx={{p: 4}}>
        <Text size={14}>Snippet not found.</Text>
      </Box>
    );
  }

  const isCustomerIoSourced = existing !== undefined && existing.source !== "manual";

  return (
    <Box sx={{p: 4, display: "flex", flexDirection: "column", gap: 2}}>
      <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>
        <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
          <IconButton onClick={() => navigate(reachUrls.snippets)}>
            <ArrowBackIcon sx={{width: 20, height: 20, color: Colors.grey._700}} />
          </IconButton>
          <Text size={24} variant="interDisplay600">
            {isEdit ? existing?.name : "New snippet"}
          </Text>
        </Box>
        <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
          {isEdit && (
            <Tooltip title="Delete snippet">
              <IconButton
                onClick={onDelete}
                disabled={isBusy}
                sx={{
                  border: `1px solid ${Colors.red._300}`,
                  borderRadius: "8px",
                  padding: "6px",
                  color: Colors.red._600,
                  "& svg path": {stroke: "currentColor"},
                  "&:hover": {backgroundColor: Colors.red._100, color: Colors.red._800},
                }}
              >
                <Box component={IconTrash} sx={{width: 20, height: 20}} />
              </IconButton>
            </Tooltip>
          )}
          <Button buttonType="contained-primary" size="small" onClick={onSave} disabled={isBusy || !formState.isDirty}>
            {isEdit ? "Save" : "Create"}
          </Button>
        </Box>
      </Box>

      {isCustomerIoSourced && (
        <Text size={13} sx={{color: Colors.grey._700}}>
          This snippet is synced from Customer.io. Editing it here will keep your version until the next sync overwrites
          it.
        </Text>
      )}

      <Grid container spacing={2}>
        <Grid size={{xs: 12, md: 6}}>
          <Card sx={{p: 3, display: "flex", flexDirection: "column", gap: 2}}>
            <Box sx={{width: "100%"}}>
              <FieldLabel>Name *</FieldLabel>
              <Controller
                name="name"
                control={control}
                render={({field: {ref, ...field}, fieldState}) => (
                  <TextField
                    {...field}
                    inputRef={ref}
                    fullWidth
                    size="small"
                    placeholder="Email Footer"
                    error={!!fieldState.error}
                  />
                )}
              />
            </Box>
            <Container row fullWidth sx={{gap: 1.5, alignItems: "flex-start", flexWrap: "nowrap"}}>
              <Box sx={{flex: 1, minWidth: 0}}>
                <FieldLabel>Source *</FieldLabel>
                <Controller
                  name="source"
                  control={control}
                  render={({field}) => (
                    <Select {...field} fullWidth size="small" displayEmpty>
                      {SOURCES.map((source) => (
                        <MenuItem key={source} value={source}>
                          {SNIPPET_SOURCE_DISPLAY[source].label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </Box>
              <Box sx={{flex: 1, minWidth: 0}}>
                <FieldLabel>Language *</FieldLabel>
                <Controller
                  name="language"
                  control={control}
                  render={({field: {onChange, ...field}}) => (
                    <Select
                      {...field}
                      fullWidth
                      size="small"
                      displayEmpty
                      onChange={(event) => {
                        onChange(event);
                        if (event.target.value === "es") {
                          setValue("translation_snippet_id", null, {shouldDirty: true});
                        } else {
                          setValue("source_snippet_id", null, {shouldDirty: true});
                        }
                      }}
                    >
                      {SNIPPET_LANGUAGES.map((option) => (
                        <MenuItem key={option} value={option}>
                          {SNIPPET_LANGUAGE_LABELS[option]}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </Box>
            </Container>
            {isSpanish && (
              <Box sx={{width: "100%"}}>
                <FieldLabel>English snippet - the snippet this one translates (optional)</FieldLabel>
                <Controller
                  name="source_snippet_id"
                  control={control}
                  render={({field: {onChange}}) => (
                    <Autocomplete<ReachSnippet, false, false, false>
                      size="small"
                      fullWidth
                      popupIcon={<IconChevronUp style={{width: 20, height: 20}} />}
                      sx={autocompleteChevronSx}
                      options={englishSnippets}
                      value={selectedSourceSnippet}
                      onChange={(_, snippet) => onChange(snippet?.id ?? null)}
                      getOptionLabel={(snippet) => snippet.name}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      noOptionsText="No English snippets found"
                      renderInput={(params) => <TextField {...params} placeholder="Search English snippets..." />}
                    />
                  )}
                />
                {selectedSourceSnippet && (
                  <Box sx={{mt: 0.75}}>
                    <SnippetLink
                      label={`Open "${selectedSourceSnippet.name}"`}
                      href={snippetUrl(selectedSourceSnippet.id)}
                    />
                  </Box>
                )}
              </Box>
            )}
            {!isSpanish && (
              <Box sx={{width: "100%"}}>
                <FieldLabel>Spanish snippet - the translation of this snippet (optional)</FieldLabel>
                <Controller
                  name="translation_snippet_id"
                  control={control}
                  render={({field: {onChange}}) => (
                    <Autocomplete<ReachSnippet, false, false, false>
                      size="small"
                      fullWidth
                      popupIcon={<IconChevronUp style={{width: 20, height: 20}} />}
                      sx={autocompleteChevronSx}
                      options={spanishSnippets}
                      value={selectedTranslationSnippet}
                      onChange={(_, snippet) => onChange(snippet?.id ?? null)}
                      getOptionLabel={(snippet) => snippet.name}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      noOptionsText="No Spanish snippets found"
                      renderInput={(params) => <TextField {...params} placeholder="Search Spanish snippets..." />}
                    />
                  )}
                />
                {selectedTranslationSnippet && (
                  <Box sx={{mt: 0.75}}>
                    <SnippetLink
                      label={`Open "${selectedTranslationSnippet.name}"`}
                      href={snippetUrl(selectedTranslationSnippet.id)}
                    />
                  </Box>
                )}
              </Box>
            )}
            <Box sx={{width: "100%"}}>
              <FieldLabel>Element - paste this into a template to embed the snippet</FieldLabel>
              <Container row fullWidth sx={{gap: 1.5, alignItems: "center", flexWrap: "nowrap"}}>
                <Controller
                  name="element_name"
                  control={control}
                  render={({field: {ref, ...field}}) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      size="small"
                      sx={{flex: 1, minWidth: 0}}
                      placeholder="{{snippets.account_details}}"
                      slotProps={{htmlInput: {readOnly: true}}}
                      InputSx={{
                        fontFamily: "monospace",
                        backgroundColor: Colors.tertiary._100,
                        "& input": {color: Colors.grey._700, caretColor: "transparent"},
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: Colors.tertiary._300,
                          borderWidth: "1px",
                        },
                      }}
                    />
                  )}
                />
                {source !== "manual" && <SyncButton phase={syncPhase} onClick={onSyncFromCio} disabled={isBusy} />}
              </Container>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                minHeight: 36,
                width: "100%",
              }}
            >
              <Text size={13} sx={{color: Colors.grey._700}}>
                HTML
              </Text>
              <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                {showHtml && (
                  <Button buttonType="outlined-primary" size="small" onClick={onFormat}>
                    Format
                  </Button>
                )}
                <Switch size="small" checked={showHtml} onChange={(e) => setShowHtml(e.target.checked)} />
              </Box>
            </Box>
            {showHtml && (
              <Controller
                name="body"
                control={control}
                render={({field: {ref, onChange, ...field}, fieldState}) => (
                  <HtmlCodeEditor
                    value={field.value}
                    onChange={onChange}
                    onBlur={field.onBlur}
                    inputRef={ref}
                    minRows={BODY_MIN_ROWS}
                    placeholder="<div>...</div>"
                    error={!!fieldState.error}
                  />
                )}
              />
            )}
          </Card>
        </Grid>
        <Grid size={{xs: 12, md: 6}}>
          <Card sx={{p: 3, display: "flex", flexDirection: "column", gap: 1.5, height: "100%"}}>
            {isEdit && <SnippetUsagePanel snippetId={snippetId} />}
            <Text size={13} sx={{color: Colors.grey._700}}>
              Preview
            </Text>
            <HtmlFrame html={previewHtml} title="Snippet preview" sx={{flex: 1, minHeight: 400}} />
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SnippetFormPage;
